import { string } from "yup";

import { isPhoneValid } from "@whoosh/utilities/src/phone";

const PHONE_FORMATTING_CHARACTERS_REGEX = /^[0-9()+.\-\s]*$/;

export type ContactInputMode = "Email" | "Phone" | "Unsure";
export type ContactInputPreference = "Automatic" | "Email" | "Phone";

export const containsOnlyPhoneFormattingCharacters = (value: string) =>
  PHONE_FORMATTING_CHARACTERS_REGEX.test(value);

export const stripPhoneFormattingCharacters = (value: string) =>
  value.replace(/[().-\s]/g, "");

export const normalizePhoneValue = (value: string) => value.replace(/\D/g, "");

export const valueIsAtLeast2LongAndOnlyNumbers = (inputString: string) => {
  return inputString.length >= 2 && /^[0-9]+$/.test(inputString);
};

const emailValidator = string().email();
export const validateEmail = (email: string) => {
  try {
    emailValidator.validateSync(email);
    return true;
  } catch (_e) {
    return false;
  }
};

export const isEmailOrPhoneMode = (
  value: string,
  countryCode?: string
): ContactInputMode => {
  const valueWithoutSpaces = value.replace(/\s/g, "");
  const isOnlyPhoneFormatting =
    valueWithoutSpaces !== "" &&
    containsOnlyPhoneFormattingCharacters(valueWithoutSpaces);
  const normalizedPhoneCandidate = isOnlyPhoneFormatting
    ? normalizePhoneValue(valueWithoutSpaces)
    : valueWithoutSpaces;

  if (normalizedPhoneCandidate.length < 2 && !countryCode) {
    return "Unsure";
  } else if (
    (isOnlyPhoneFormatting &&
      valueIsAtLeast2LongAndOnlyNumbers(normalizedPhoneCandidate)) ||
    valueWithoutSpaces.startsWith("+") ||
    !!countryCode
  ) {
    return "Phone";
  } else {
    return "Email";
  }
};

export const getEffectiveInputMode = (
  value: string,
  countryCode: string | undefined,
  preference: ContactInputPreference
): ContactInputMode => {
  return preference === "Automatic"
    ? isEmailOrPhoneMode(value, countryCode)
    : preference;
};

export const getEmailValueOnSwitch = (value: string) => {
  return containsOnlyPhoneFormattingCharacters(value)
    ? stripPhoneFormattingCharacters(value)
    : value;
};

export const getPhoneValueOnSwitch = (value: string) => {
  return containsOnlyPhoneFormattingCharacters(value)
    ? stripPhoneFormattingCharacters(value)
    : "";
};

export const isValidToContinue = (
  value: string,
  countryCode: string,
  mode: ContactInputMode
) => {
  if (value === "" || mode === "Unsure") {
    return false;
  }

  if (mode === "Email") {
    return validateEmail(value);
  }

  const normalizedPhoneValue = normalizePhoneValue(value);
  const candidatePhoneValue = containsOnlyPhoneFormattingCharacters(value)
    ? normalizedPhoneValue
    : value;

  return (
    candidatePhoneValue !== "" && isPhoneValid(candidatePhoneValue, countryCode)
  );
};
